import { type LineItem, PackageEntrySource } from "../../models/CartData";
import { buildCartMaterialNos, newChangeObject } from "../../../../../utils/cartUpdating/common";
import {
  refreshAvailabilityLoadContract,
  refreshAvailabilityLoadNonContract,
} from "../../../../../utils/cartUpdating/refreshPackage";
import instance from "../../../../../utils/i18n";
import { addMessage } from "~/features/toasts/redux/actions";
import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { groupByFn } from "~/common/utils/reducers";
import { getContractDetails } from "~/features/contracts/api/getContractDetails";
import { refreshContractDetailsLoad } from "../../../../../utils/cartUpdating/refreshFromContract";
import { type LineItemBuilder } from "../../../../../utils/cartUpdating/LineItemUpdate";
import { refreshPriceInfoData } from "../../../../../utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveryDate } from "../../../../../utils/cartUpdating/delivery/refreshDate";
import { refreshDeliveriesForProduct } from "../../../../../utils/cartUpdating/delivery/refreshEdd";
import { sendToHybris, sendToRedux, buildSetPackageEntriesPayload } from "./cartQuoteCommonActions";
import { type RootState } from "~/app/store";
import { validatePackage, validateQty } from "../../../../../utils/cartUpdating/validations/validateGeneralLineItem";
import { validateEntireProductQty } from "../../../../../utils/cartUpdating/validations/validateGeneralProduct";
import { isConfiguratorProduct } from "~/domains/product/types/ProductData";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import * as pnaUpdateAccumulator from "../pnaUpdateAccumulator";
import {
  buildCartDescriptorsFromPairs,
  cartAvailabilityFromCache,
  loadCartAvailability,
  loadCartPna,
  loadCartPrice,
  quotePackCodeFromBuilders,
  type CartPnaPair,
} from "../../pna/cartPna";
import { buildProductDescriptor } from "~/domains/pna/descriptor";
import type { PriceInfo } from "~/domains/pricing/api/PriceInfo";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

const CONTRACT_DETAILS_KEY_FN = (item: LineItemBuilder) =>
  `${item.get("contractNumber")}-${item.get("contractLine")}-${item.get("packCode")}`;

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
type PnaRefreshOptions = { preserveServerPrices?: boolean; onPersistenceSettled?: () => Promise<void> };

function shouldPersistRefreshedItems(lineItems: LineItemBuilder[], options?: PnaRefreshOptions): boolean {
  return !options?.preserveServerPrices || lineItems.some((item) => item.hasUpdatesForSending());
}

function sendToReduxViaAccumulator(lineItems: LineItemBuilder[], gen: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const data = buildSetPackageEntriesPayload(lineItems, getState().cart);
    if (data.length === 0) {
      return;
    }
    pnaUpdateAccumulator.enqueue(dispatch, data, gen);
  };
}

function dispatchFreshPnaMessage(prices: PriceInfo[][], availabilityFresh?: boolean): ThunkResult<void> {
  return (dispatch, getState) => {
    const isFresh = availabilityFresh || prices.flat().some((pInfo) => pInfo.isFreshPnaCall);
    if (!isFresh) {
      return;
    }
    const message = instance.t("cartPage.globalMsg.freshPna");
    const hasFreshMessage = getState().globalMessages.some((msg) => msg.message.value === message);
    if (!hasFreshMessage) {
      dispatch(addMessage("info", message));
    }
  };
}

export function orchestratePnARefresh(
  packageEntries: LineItem[],
  accumulatorGen?: number,
  activeCartId?: string,
  options?: PnaRefreshOptions,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;
    const base = getState().products.basic[productCode];

    if (isConfiguratorProduct(base)) {
      return dispatch(orchestratePnARefreshLinear(packageEntries, accumulatorGen, activeCartId, options));
    } else {
      return dispatch(orchestratePnARefreshRegular(packageEntries, accumulatorGen, options));
    }
  };
}

function orchestratePnARefreshLinear(
  packageEntries: LineItem[],
  accumulatorGen?: number,
  activeCartId?: string,
  options?: PnaRefreshOptions,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;
    const base = getState().products.basic[productCode];

    const allLineItems = packageEntries.map(newChangeObject);

    const priceReferences: Dict<boolean> = {};
    allLineItems.forEach((entry) => (priceReferences[entry.get("priceReference")] = true));

    // Availability warms the shared query fire-and-forget (legacy dispatched it un-awaited):
    // a slow or failed availability side must never delay or suppress the price refresh.
    loadCartAvailability({ productCode, priceReference: "", base, activeCartId }).catch(() => undefined);

    let prices: PriceInfo[][];
    try {
      prices = await Promise.all(
        Object.keys(priceReferences).map((priceReference) =>
          loadCartPrice({ productCode, priceReference, base, activeCartId }),
        ),
      );
    } catch {
      // Superseded work (cancelAndClearPna aborts the queries) or a failed price transport:
      // settle silently — no error UI action, no Redux update.
      return;
    }

    // Show a notification that the prices may have changed...
    dispatch(dispatchFreshPnaMessage(prices));

    // Prices are the awaited values; the un-awaited availability side contributes its current
    // cache state under the same load identity (the legacy dictionary read).
    const availabilityOutcome = cartAvailabilityFromCache({ productCode, priceReference: "", base, activeCartId });
    const descriptorsByPriceReference = Object.keys(priceReferences).reduce((map, priceReference, index) => {
      map[priceReference] = buildProductDescriptor(
        base,
        { price: { status: "success", data: prices[index] }, availability: availabilityOutcome },
        priceReference,
      );
      return map;
    }, {} as Dict<ProductDescriptor>);

    // 2. Take care of non contract items.

    // 2.2. Then data from PriceInfo objects (price & Country of Origin).
    if (!options?.preserveServerPrices) {
      refreshPriceInfoData(allLineItems, descriptorsByPriceReference);
    }

    // 5. Various qty and package validations
    allLineItems.forEach(validateQty);
    validateEntireProductQty(
      allLineItems,
      Object.values(descriptorsByPriceReference)[0],
      buildCartMaterialNos(getState()),
    );
    allLineItems.forEach(validatePackage);

    if (accumulatorGen !== undefined) {
      dispatch(sendToReduxViaAccumulator(allLineItems, accumulatorGen));
    } else {
      dispatch(sendToRedux(allLineItems));
    }
    if (shouldPersistRefreshedItems(allLineItems, options)) {
      await dispatch(sendToHybris(allLineItems));
      if (options?.preserveServerPrices) {
        await options.onPersistenceSettled?.();
      }
    }
  };
}

function orchestratePnARefreshRegular(
  packageEntries: LineItem[],
  accumulatorGen?: number,
  options?: PnaRefreshOptions,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;
    const base = getState().products.basic[productCode];

    const allLineItems = packageEntries.map(newChangeObject);
    const lineItemsByType = groupByFn(
      allLineItems,
      (item) => item.get("source"),
      (item) => item,
    );
    const lineItemsWithUpdateablePrice = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
    ];
    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];
    const contractLineItems = [...(lineItemsByType[PackageEntrySource.CONTRACT] || [])];

    // We assume that all line items will have the same pack code in this case. This is normally a PMA product but the quote provided a packCode
    const fifteenDigitPackCode = quotePackCodeFromBuilders(allLineItems);

    const priceReferences: Dict<boolean> = {};
    allLineItems.forEach((entry) => (priceReferences[entry.get("priceReference")] = true));

    // Fetch Contract Details
    const byContractDetails = groupByFn(contractLineItems, CONTRACT_DETAILS_KEY_FN);
    const contractPromises = Object.values(byContractDetails)
      .map((list) => list[0])
      .map((lineItem) =>
        getContractDetails(
          lineItem.get("contractNumber"),
          base.sapMaterialNumber,
          lineItem.get("packCode"),
          +lineItem.get("contractLine"),
        ),
      );

    let pairs: CartPnaPair[];
    let contractDetails: (ContractDetailsResponse | null)[];
    try {
      [pairs, contractDetails] = await Promise.all([
        Promise.all(
          Object.keys(priceReferences).map((priceReference) =>
            loadCartPna({ productCode, priceReference, base, packCode: fifteenDigitPackCode }),
          ),
        ),
        Promise.all(contractPromises),
      ]);
    } catch {
      // Superseded work (cancelAndClearPna aborts the queries) or a failed transport:
      // settle silently — no error UI action, no Redux update.
      return;
    }
    const availability = pairs[0]?.availability;

    // Show a notification that the prices may have changed...
    dispatch(
      dispatchFreshPnaMessage(
        pairs.map((pair) => pair.price),
        availability?.isFreshPnaCall,
      ),
    );

    // Let the fun with refreshing begin...
    const descriptorsByPriceReference = buildCartDescriptorsFromPairs(
      getState(),
      productCode,
      Object.keys(priceReferences),
      pairs,
    );

    // 1. Take care of contract items. They are simple(r)
    const contractDetailsByKey = recreateContractDetailsMap(byContractDetails, contractDetails);

    contractLineItems.forEach((lineItem) =>
      refreshContractDetailsLoad(
        lineItem,
        descriptorsByPriceReference[lineItem.get("priceReference")],
        contractDetailsByKey[CONTRACT_DETAILS_KEY_FN(lineItem)]!,
      ),
    );

    if (availability) {
      contractLineItems.forEach((lineItem) =>
        refreshAvailabilityLoadContract(
          lineItem,
          descriptorsByPriceReference[lineItem.get("priceReference")].availability!,
        ),
      );

      // 2. Take care of non contract items.

      // 2.1. First availability => We may need to preselect packages.
      lineItemsWithUpdateablePrice.forEach((lineItem) => {
        const descriptor = descriptorsByPriceReference[lineItem.get("priceReference")];
        refreshAvailabilityLoadNonContract(lineItem, descriptor.availability!, descriptor);
      });
    }

    // 2.2. Then data from PriceInfo objects (price & Country of Origin).
    if (!options?.preserveServerPrices) {
      refreshPriceInfoData(allLineItems, descriptorsByPriceReference);
    }

    // 3. Refresh the Estimated Delivery Date.
    if (availability) {
      refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability);
    }

    // 4. Refresh requested delivery date
    allLineItems.forEach(refreshDeliveryDate);

    // 4b. Repeat - Refresh the Estimated Delivery Date.
    if (availability) {
      refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability);
    }

    // 5. Various qty and package validations
    allLineItems.forEach(validateQty);
    validateEntireProductQty(
      allLineItems,
      Object.values(descriptorsByPriceReference)[0],
      buildCartMaterialNos(getState()),
    );
    allLineItems.forEach(validatePackage);

    if (accumulatorGen !== undefined) {
      dispatch(sendToReduxViaAccumulator(allLineItems, accumulatorGen));
    } else {
      dispatch(sendToRedux(allLineItems));
    }
    if (shouldPersistRefreshedItems(allLineItems, options)) {
      await dispatch(sendToHybris(allLineItems));
      if (options?.preserveServerPrices) {
        await options.onPersistenceSettled?.();
      }
    }
  };
}

export function orchestrateARefreshOnCheckout(
  packageEntries: LineItem[],
  accumulatorGen?: number,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const productCode = packageEntries[0].productCode;

    // Nothing to refresh for linear products
    // Fixes SCHAEFF-12563
    const productData = getState().products.basic[productCode];
    if (isConfiguratorProduct(productData)) {
      // Fire-and-forget AVAILABILITY-ONLY warmup, mirroring the legacy un-awaited dispatch —
      // the checkout linear path transports no price at all (A07 review).
      loadCartAvailability({ productCode, priceReference: "", base: productData }).catch(() => undefined);
      return;
    }

    const allLineItems = packageEntries.map(newChangeObject);
    const lineItemsByType = groupByFn(
      allLineItems,
      (item) => item.get("source"),
      (item) => item,
    );
    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];

    const is15DigitACall = allLineItems.some((lineItem) => lineItem.get("source") === PackageEntrySource.QUOTE);
    // We assume that all line items will have the same pack code in this case. This is normally a PMA product but the quote provided a packCode
    const fifteenDigitPackCode = is15DigitACall ? allLineItems[0]?.get("packCode") : undefined;

    const priceReferences: Dict<boolean> = {};
    allLineItems.forEach((entry) => (priceReferences[entry.get("priceReference")] = true));

    // SCHAEFF-16550 - prices ride along on the same queries (fire-and-forget, silent catch) so
    // the contract icon can appear; only the availability side gates EDD publication and
    // persistence, restoring the legacy await scope and failure semantics.
    Object.keys(priceReferences).forEach((priceReference) => {
      loadCartPna({ productCode, priceReference, base: productData, packCode: fifteenDigitPackCode }).catch(
        () => undefined,
      );
    });

    let availability;
    try {
      // Availability is price-reference independent (its key carries only code + packCode).
      availability = await loadCartAvailability({
        productCode,
        priceReference: "",
        base: productData,
        packCode: fifteenDigitPackCode,
      });
    } catch {
      // Superseded work (cancelAndClearPna aborts the queries) or a failed availability
      // transport: settle silently — no error UI action, no Redux update.
      return;
    }

    // 3. Refresh the Estimated Delivery Date.
    if (availability) {
      refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability);
    }

    if (accumulatorGen !== undefined) {
      dispatch(sendToReduxViaAccumulator(allLineItems, accumulatorGen));
    } else {
      dispatch(sendToRedux(allLineItems));
    }
    // dispatch(sendToHybris(allLineItems));
  };
}

function recreateContractDetailsMap(
  byContractDetails: Dict<LineItemBuilder[]>,
  contractDetails: (ContractDetailsResponse | null)[],
): Dict<ContractDetailsResponse | null> {
  return Object.keys(byContractDetails)
    .map((key, index) => [key, index] as [string, number])
    .reduce(
      (map, [key, index]) => {
        map[key] = contractDetails[index];
        return map;
      },
      {} as Dict<ContractDetailsResponse | null>,
    );
}
